import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ArrowClockwise,
  Copy,
  SpeakerHigh,
  Stop,
  ThumbsDown,
  ThumbsUp,
} from '@phosphor-icons/react';
import { useDispatch, useSelector } from 'react-redux';
import { enqueueSnackbar } from 'notistack';

import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import Tooltip from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import {
  rateChatMessage,
  type ChatMessageRating,
} from '@/services/chatRatings';
import { messageRated } from '@/slices/chat/chatSlice';
import {
  captureCompanionRated,
  useCompanionAnalyticsSurface,
} from './companionAnalytics';
import { selectMessage, type ChatRootState } from '@/slices/chat/chatSelectors';
import { markdownToSpeakableText } from './chatText';
import { watchTokensToText } from '../shared/watchToken';
import {
  startChatAudio,
  stopChatAudio,
  type ChatAudioState,
} from './chatAudio';
import { isSpeechSynthesisAvailable } from './chatSpeech';

/**
 * The action bar under a finished answer (ORA-464, ORA-468): Copy, Good
 * response, Bad response, Try again, Read aloud.
 *
 * It is deliberately quiet — small muted icons, no labels, revealed when the
 * message is hovered or something inside it takes focus. On a device with no
 * hover it is always visible, because there is no way to reveal it otherwise;
 * and it stays visible whenever it is saying something (a rating is set, an
 * answer is being read) rather than hiding the state the student just set.
 *
 * Available buttons carry their label as both the tooltip and accessible name.
 * A disabled image retry keeps the action name and uses its tooltip to explain
 * why the original turn cannot be faithfully re-sent.
 *
 * The bar is rendered only under COMPLETED answers — `ChatMessageItem` owns
 * that rule — and each action decides for itself whether it applies:
 *
 * - **Copy** wherever there is prose to copy. It is the one thing a legacy
 *   (read-only) answer offers.
 * - **Good/Bad response** once the server has named the message, since the
 *   rating hangs off that turn's trace row.
 * - **Try again** on the latest answer only, and only while a turn can start.
 * - **Read aloud** where there is prose and the browser can speak at all.
 */

/** How long the Copy button reports success before returning to its label. */
export const COPIED_FEEDBACK_MS = 1_500;

/** How long a refused rating is reported before the button goes back. */
export const RATING_ERROR_FEEDBACK_MS = 2_500;

const RATING_ERROR = "Couldn't save rating";

export interface ChatMessageActionsProps {
  /** The message in the slice — the id Try again and the rating are keyed by. */
  messageId: string;
  /** What Copy puts on the clipboard: the answer exactly as it is written. */
  text: string;
  /**
   * The server's id for this answer. Rating hangs off the turn trace, which is
   * keyed by it, so an answer the server has not named yet cannot be rated.
   */
  serverMessageId?: string | null;
  /** Re-run this answer. Omitted when Try again does not apply here. */
  onTryAgain?: () => void;
  /** Keep Try again visible but disabled, and explain why in its tooltip. */
  tryAgainDisabledReason?: string;
  /**
   * A legacy answer is a read-only archive of a pipeline that no longer exists:
   * there is nothing to re-run, no trace to rate, and Copy is all it offers.
   */
  isLegacy?: boolean;
  /**
   * A course student's companion (Courses V1): Copy and Read aloud put a
   * `[[watch …]]` token into words. Off, the answer is taken as written.
   */
  watchTokens?: boolean;
}

const ChatMessageActions = ({
  messageId,
  text,
  serverMessageId,
  onTryAgain,
  tryAgainDisabledReason,
  isLegacy = false,
  watchTokens = false,
}: ChatMessageActionsProps) => {
  const dispatch = useDispatch();
  const analyticsSurface = useCompanionAnalyticsSurface();

  const rating = useSelector(
    (state: ChatRootState) => selectMessage(state, messageId)?.rating ?? null
  );

  const [copied, setCopied] = useState(false);
  const [audioState, setAudioState] = useState<ChatAudioState>('idle');
  /** The thumb whose write the server refused, while it is being reported. */
  const [failedRating, setFailedRating] = useState<ChatMessageRating>(null);
  /** What the bar is telling a screen reader right now. */
  const [announcement, setAnnouncement] = useState('');
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const confirmedRating = useRef<ChatMessageRating>(rating);
  const ratingSequence = useRef(0);
  const ratingWriteQueue = useRef<Promise<void>>(Promise.resolve());
  const hasText = text.trim() !== '';
  const canRate = !isLegacy && Boolean(serverMessageId);

  const canSpeak =
    !isLegacy &&
    hasText &&
    (Boolean(serverMessageId) || isSpeechSynthesisAvailable());

  const isReading = audioState === 'loading' || audioState === 'playing';

  const canTryAgain =
    !isLegacy && Boolean(onTryAgain || tryAgainDisabledReason);

  useEffect(
    () => () => {
      if (copiedTimer.current) clearTimeout(copiedTimer.current);
      if (errorTimer.current) clearTimeout(errorTimer.current);
    },
    []
  );

  /** Leaving the thread must release any callback owned by this action bar. */
  useEffect(
    () => () => {
      stopChatAudio(serverMessageId ?? messageId);
    },
    [messageId, serverMessageId]
  );

  const handleCopy = useCallback(async () => {
    try {
      // The one change to the answer as written, in a course student's
      // companion: a "Watch again" token is copied as its range in words
      // (ORA-1055); its brackets mean nothing outside the thread.
      await navigator.clipboard?.writeText(
        watchTokens ? watchTokensToText(text) : text
      );
    } catch {
      // A refused clipboard (no permission, an insecure origin) is not worth
      // an error state on an answer the student has already read.
      return;
    }

    setCopied(true);
    if (copiedTimer.current) clearTimeout(copiedTimer.current);

    copiedTimer.current = setTimeout(
      () => setCopied(false),
      COPIED_FEEDBACK_MS
    );
  }, [text]);

  const handleRate = useCallback(
    (choice: Exclude<ChatMessageRating, null>) => {
      if (!serverMessageId) return;

      // Tapping the thumb that is already lit retracts it: one control, both
      // directions, and no separate "clear" affordance to explain.
      const next: ChatMessageRating = rating === choice ? null : choice;
      const sequence = ++ratingSequence.current;

      // Optimistic: the highlight is the student's own choice and must not
      // wait on a round trip.
      dispatch(messageRated({ messageId, rating: next }));
      setFailedRating(null);
      setAnnouncement('');

      // Keep writes in click order so the server ends on the latest choice,
      // while leaving both controls available for another immediate choice.
      ratingWriteQueue.current = ratingWriteQueue.current.then(async () => {
        const didSave = await rateChatMessage(serverMessageId, next);

        if (didSave) {
          confirmedRating.current = next;
          // Counted once the server has it, so a refused write that the bar
          // puts back is not a rating (ORA-1118).
          captureCompanionRated({ rating: next, surface: analyticsSurface });
          return;
        }

        // A superseded failure must not overwrite a newer optimistic choice.
        if (sequence !== ratingSequence.current) return;

        // The server refused the latest choice, so put back the most recent
        // rating it accepted and say so.
        dispatch(messageRated({ messageId, rating: confirmedRating.current }));
        setFailedRating(choice);
        setAnnouncement(RATING_ERROR);

        if (errorTimer.current) clearTimeout(errorTimer.current);

        errorTimer.current = setTimeout(
          () => setFailedRating(null),
          RATING_ERROR_FEEDBACK_MS
        );
      });
    },
    [analyticsSurface, dispatch, messageId, rating, serverMessageId]
  );

  const handleReadAloud = useCallback(() => {
    if (isReading) {
      stopChatAudio(serverMessageId ?? messageId);
      return;
    }

    setAnnouncement('');

    startChatAudio({
      messageId: serverMessageId ?? messageId,
      text: markdownToSpeakableText(text, { watchTokens }),
      useServerAudio: Boolean(serverMessageId),
      onStateChange: setAudioState,
      onNotice: (notice) => {
        setAnnouncement(notice);
        enqueueSnackbar(notice, { variant: 'info' });
      },
    });
  }, [isReading, messageId, serverMessageId, text]);

  useEffect(() => {
    if (!isReading) return;

    const stopOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      stopChatAudio(serverMessageId ?? messageId);
    };

    window.addEventListener('keydown', stopOnEscape, { capture: true });

    return () =>
      window.removeEventListener('keydown', stopOnEscape, { capture: true });
  }, [isReading, messageId, serverMessageId]);

  const iconButtonClassName =
    'size-9 text-fg-secondary hover:text-fg-primary mobile:size-8';

  const ratingLabel = (choice: Exclude<ChatMessageRating, null>) => {
    if (failedRating === choice) return RATING_ERROR;
    return choice === 'up' ? 'Good response' : 'Bad response';
  };

  // A blocks-only answer with nothing to re-run and nothing to rate has no bar
  // to show; rendering an empty row would still take vertical space.
  if (!hasText && !canRate && !canTryAgain) return null;

  return (
    <div
      className="flex flex-wrap items-center gap-x-0.5 gap-y-0.5"
      data-testid="chat-message-actions"
    >
      {hasText ? (
        <Tooltip title={copied ? 'Copied' : 'Copy response'} placement="top">
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            className={iconButtonClassName}
            aria-label={copied ? 'Copied' : 'Copy response'}
            onClick={() => void handleCopy()}
          >
            <Copy size={16} aria-hidden="true" />
          </Button>
        </Tooltip>
      ) : null}

      {canRate ? (
        <>
          <Tooltip title={ratingLabel('up')} placement="top">
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              pressed={rating === 'up'}
              className={iconButtonClassName}
              aria-label={ratingLabel('up')}
              aria-pressed={rating === 'up'}
              onClick={() => void handleRate('up')}
            >
              <ThumbsUp
                size={16}
                weight={rating === 'up' ? 'fill' : 'regular'}
                aria-hidden="true"
              />
            </Button>
          </Tooltip>
          <Tooltip title={ratingLabel('down')} placement="top">
            <Button
              type="button"
              size="icon-sm"
              variant="ghost"
              pressed={rating === 'down'}
              className={iconButtonClassName}
              aria-label={ratingLabel('down')}
              aria-pressed={rating === 'down'}
              onClick={() => void handleRate('down')}
            >
              <ThumbsDown
                size={16}
                weight={rating === 'down' ? 'fill' : 'regular'}
                aria-hidden="true"
              />
            </Button>
          </Tooltip>
        </>
      ) : null}

      {canTryAgain ? (
        <Tooltip title={tryAgainDisabledReason ?? 'Try again'} placement="top">
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            className={cn(
              iconButtonClassName,
              tryAgainDisabledReason && 'cursor-not-allowed opacity-50'
            )}
            aria-label="Try again"
            aria-disabled={Boolean(tryAgainDisabledReason)}
            onClick={tryAgainDisabledReason ? undefined : onTryAgain}
          >
            <ArrowClockwise size={16} aria-hidden="true" />
          </Button>
        </Tooltip>
      ) : null}

      {canSpeak ? (
        <Tooltip
          title={
            audioState === 'loading'
              ? 'Loading audio'
              : audioState === 'playing'
                ? 'Stop reading'
                : 'Read aloud'
          }
          placement="top"
        >
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            className={iconButtonClassName}
            aria-label={
              audioState === 'loading'
                ? 'Loading audio'
                : audioState === 'playing'
                  ? 'Stop reading'
                  : 'Read aloud'
            }
            aria-busy={audioState === 'loading' || undefined}
            onClick={handleReadAloud}
          >
            {audioState === 'loading' ? (
              <Spinner size={16} aria-hidden="true" />
            ) : audioState === 'playing' ? (
              <Stop size={16} weight="fill" aria-hidden="true" />
            ) : (
              <SpeakerHigh size={16} aria-hidden="true" />
            )}
          </Button>
        </Tooltip>
      ) : null}

      {/* A refused write is reported even if focus is on another action. */}
      <span role="status" aria-live="polite" className="sr-only">
        {announcement}
      </span>
    </div>
  );
};

export default ChatMessageActions;
